import { useEffect, useSyncExternalStore } from 'react';

/**
 * Hash router. Mini Apps reopen at their entry URL and Telegram's BackButton
 * maps to history.back(), so a hash stack is the simplest thing that keeps
 * deep links (#/c/<cheque>) and back navigation honest in both hosts.
 */
export interface Route {
  path: string;
  segments: string[];
  query: URLSearchParams;
}

function read(): Route {
  const raw = location.hash.replace(/^#/, '') || '/';
  const [path = '/', qs = ''] = raw.split('?');
  return { path, segments: path.split('/').filter(Boolean), query: new URLSearchParams(qs) };
}

let current = read();
let depth = Number(history.state?.depth ?? 0);
const listeners = new Set<() => void>();

window.addEventListener('hashchange', () => {
  current = read();
  depth = Number(history.state?.depth ?? 0);
  listeners.forEach((l) => l());
});

export function useRoute(): Route {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => current,
  );
}

export function navigate(path: string, opts: { replace?: boolean } = {}) {
  const url = `#${path}`;
  if (opts.replace) {
    history.replaceState({ depth }, '', url);
  } else {
    depth += 1;
    history.pushState({ depth }, '', url);
  }
  current = read();
  listeners.forEach((l) => l());
}

export const canGoBack = () => depth > 0;

export function back(fallback = '/') {
  if (canGoBack()) history.back();
  else navigate(fallback, { replace: true });
}

/** Tab switches reset the stack so Back never walks across tabs. */
export function switchTab(path: string) {
  depth = 0;
  history.replaceState({ depth: 0 }, '', `#${path}`);
  current = read();
  listeners.forEach((l) => l());
}

export function useScrollTopOnRoute(route: Route) {
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [route.path]);
}
